/**
 * MarqueePlane — the SELECTION-mode drag rectangle.
 *
 * Rendered as a SCREEN-SPACE overlay (outside the world transform) so the
 * marquee tracks the fingers 1:1 regardless of zoom. Geometry derives from
 * shared values the marquee worklet writes; selection resolution happens
 * once, on gesture end, on the JS thread.
 *
 * @module floorplan/renderer/MarqueePlane
 * @category FloorPlan
 */

import type { SharedValue } from 'react-native-reanimated';
import { useDerivedValue } from 'react-native-reanimated';
import { Rect } from '@shopify/react-native-skia';

/** Shared-value bundle the marquee gesture writes (screen coords). */
export type MarqueeChannels = {
  active: SharedValue<number>;
  startX: SharedValue<number>;
  startY: SharedValue<number>;
  currentX: SharedValue<number>;
  currentY: SharedValue<number>;
};

type Props = {
  marquee: MarqueeChannels;
  /** Marquee color (theme.selection). */
  color: string;
};

const MarqueePlane = ({ marquee, color }: Props) => {
  const x = useDerivedValue(() =>
    Math.min(marquee.startX.get(), marquee.currentX.get()),
  );
  const y = useDerivedValue(() =>
    Math.min(marquee.startY.get(), marquee.currentY.get()),
  );
  const width = useDerivedValue(() =>
    Math.abs(marquee.currentX.get() - marquee.startX.get()),
  );
  const height = useDerivedValue(() =>
    Math.abs(marquee.currentY.get() - marquee.startY.get()),
  );
  const fillOpacity = useDerivedValue(() => marquee.active.get() * 0.12);
  const strokeOpacity = useDerivedValue(() => marquee.active.get() * 0.9);

  return (
    <>
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        color={color}
        opacity={fillOpacity}
      />
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        color={color}
        style="stroke"
        strokeWidth={1.5}
        opacity={strokeOpacity}
      />
    </>
  );
};

export default MarqueePlane;
